import * as React from 'react';
import {FunctionComponent, useState} from 'react';
import {FullWorkoutPartResult, PartType} from '../../../../Models/FullWorkout';
import {formatDuration} from '../../../Support/Formatting/formatDuration';
import {formatDistance} from '../../../Support/Formatting/formatDistance';
import {formatAverageSplit} from '../../../Support/Formatting/formatSplit';
import {Speed} from '../../../../Domain/Speed';
import {formatAverageStrokeRate} from '../../../Support/Formatting/formatStrokeRate';
import {formatAveragePower} from '../../../Support/Formatting/formatPower';
import {Bucket} from '../../Utils/Bucket';
import {formatAverageStrokeLength} from '../../../Support/Formatting/formatStrokeLength';
import {formatAveragePeakForce} from '../../../Support/Formatting/formatPeakForce';
import {formatEnergy} from '../../../Support/Formatting/formatEnergy';
import {formatDriveTime} from '../../../Support/Formatting/formatDriveTime';
import {formatRecoverTime} from '../../../Support/Formatting/formatRecoverTime';
import {formatAveragePowerSplit} from '../../../Support/Formatting/formatPowerSplit';
import {classNames} from '../../../Support/classNames';
import {UnrolledPart} from '../../../../Business/unrollParts';
import {formatToKiloCalories} from '../../../Support/Formatting/formatCalories';
import {FeatureFlags} from '../../../../FeatureFlags';
import {PowerMode, useSettings} from '../../../Settings/settings';
import {formatAverageRelativePeakForcePosition} from '../../../Support/Formatting/formatRelativePeakForcePosition';

interface OwnProps {
    initiallyExpanded: boolean;
    fullWorkoutPartResult: FullWorkoutPartResult;
    unrolledPart: UnrolledPart | null;
    selected: boolean;
    onSelectClick: () => void;
    onDeSelectClick: () => void;
    isOvershoot: boolean;
}

export const IndoorIntervalTableRow: FunctionComponent<OwnProps> = ({
    initiallyExpanded,
    fullWorkoutPartResult,
    unrolledPart,
    onSelectClick,
    onDeSelectClick,
    isOvershoot,
    selected,
}) => {
    const [expanded, setExpanded] = useState(initiallyExpanded);

    const dimmed = !expanded && (isOvershoot || (unrolledPart && unrolledPart.type === PartType.Rest));

    const {powerMode} = useSettings();
    const getPowerLabel = () => {
        return powerMode === PowerMode.Split ? 'Ø Split Power (W)' : 'Ø Power (W)';
    };

    return (
        <>
            <tr
                className={classNames('intervaltable__row', {
                    'intervaltable__row--active': expanded,
                    'intervaltable__row--dimmed': dimmed,
                    'intervaltable__row--highlighted': selected,
                })}
                onClick={() => setExpanded(!expanded)}
            >
                {FeatureFlags.rangeAnalyzer && (
                    <td className="intervaltable__cell intervaltable__cell--narrow">
                        <button
                            className={classNames('btn btn-sm', {
                                'btn-outline-primary': !selected,
                                'btn-primary': selected,
                            })}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (selected) {
                                    onDeSelectClick();
                                } else {
                                    onSelectClick();
                                }
                            }}
                        >
                            <i className="bi bi-graph-up" />
                        </button>
                    </td>
                )}
                <td
                    className={classNames('intervaltable__cell', {
                        'intervaltable__cell--highlighted': unrolledPart && unrolledPart.type === PartType.Duration,
                    })}
                >
                    {formatDuration(fullWorkoutPartResult.elapsed_time).formatted}
                </td>
                <td
                    className={classNames('intervaltable__cell intervaltable__cell--rightalined', {
                        'intervaltable__cell--highlighted': unrolledPart && unrolledPart.type === PartType.Distance,
                    })}
                >
                    {formatDistance(fullWorkoutPartResult.traveled_distance).formatted}
                </td>
                <td className="intervaltable__cell intervaltable__cell--rightalined">
                    {formatAverageSplit(Speed.fromDurationPer500Meters(fullWorkoutPartResult.average_split)).formatted}
                </td>
                <td className="intervaltable__cell intervaltable__cell--rightalined">
                    {formatAverageStrokeRate(fullWorkoutPartResult.average_stroke_rate).formatted}
                </td>
                <td className="intervaltable__cell intervaltable__cell--rightalined">
                    {powerMode === PowerMode.Split
                        ? formatAveragePowerSplit(Speed.fromDurationPer500Meters(fullWorkoutPartResult.average_split))
                              .formatted
                        : formatAveragePower(fullWorkoutPartResult.average_power).formatted}
                </td>
                <td>
                    <button
                        className={classNames('intervaltable__row-expandbutton', {
                            'intervaltable__row-expandbutton--active': expanded,
                        })}
                        onClick={() => setExpanded(!expanded)}
                    >
                        <i className="bi bi-chevron-compact-down" />
                    </button>
                </td>
            </tr>
            <tr className={classNames('intervaltable__row-details', {'intervaltable__row-details--active': expanded})}>
                <td colSpan={6}>
                    <div className="row d-flex flex-column gap-2">
                        <div className="col-12  d-flex flex-row gap-2">
                            <Bucket
                                small
                                value={formatAverageSplit(
                                    Speed.fromDurationPer500Meters(fullWorkoutPartResult.average_split),
                                )}
                                label="Ø Split"
                            />
                            <Bucket
                                small
                                value={
                                    powerMode === PowerMode.Split
                                        ? formatAveragePowerSplit(
                                              Speed.fromDurationPer500Meters(fullWorkoutPartResult.average_split),
                                          )
                                        : formatAveragePower(fullWorkoutPartResult.average_power)
                                }
                                label={getPowerLabel()}
                            />
                            <Bucket
                                small
                                value={formatAveragePeakForce(fullWorkoutPartResult.average_peak_force)}
                                label="Ø Peak force"
                            />
                        </div>
                        <div className="col-12  d-flex flex-row gap-2">
                            <Bucket
                                small
                                value={formatAverageRelativePeakForcePosition(
                                    fullWorkoutPartResult.average_relative_peak_force_position,
                                )}
                                label="Ø Rel. peak force pos."
                            />
                            <Bucket
                                small
                                value={formatAverageStrokeLength(fullWorkoutPartResult.average_stroke_length)}
                                label="Ø Stroke length"
                            />
                            <Bucket
                                small
                                value={formatEnergy(fullWorkoutPartResult.total_energy)}
                                label="Total energy"
                            />
                        </div>
                        <div className="col-12  d-flex flex-row gap-2">
                            <Bucket
                                small
                                value={formatToKiloCalories(fullWorkoutPartResult.burned_calories)}
                                label="Burned calories"
                            />
                            <Bucket
                                small
                                value={formatDriveTime(fullWorkoutPartResult.average_drive_time)}
                                label="Ø Drive time"
                            />
                            <Bucket
                                small
                                value={formatRecoverTime(fullWorkoutPartResult.average_recover_time)}
                                label="Ø Recover time"
                            />
                        </div>
                    </div>
                </td>
            </tr>
        </>
    );
};
